import { useState, useRef, useEffect } from "react";
import { useAuth } from "./auth";
import { apiError } from "./api";
import { Mail, Check } from "lucide-react";

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
const BTN = { background: "linear-gradient(90deg,#4338ca,#db2777)" };

function OtpBoxes({ value, onChange, onComplete, success }) {
  const refs = useRef([]);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");

  const setAt = (i, ch) => {
    const next = value.padEnd(6, " ").slice(0, 6).split("");
    next[i] = ch || " ";
    const clean = next.map((c) => (c === " " ? "" : c)).join("");
    onChange(clean);
    if (clean.length === 6) onComplete?.(clean);
  };

  const handleChange = (i, e) => {
    const v = e.target.value.replace(/\D/g, "");
    if (!v) { setAt(i, ""); return; }
    if (v.length > 1) {
      // typed/pasted multiple — distribute from this box
      const chars = v.slice(0, 6 - i).split("");
      const arr = value.padEnd(6, " ").slice(0, 6).split("");
      chars.forEach((c, k) => { arr[i + k] = c; });
      const clean = arr.map((c) => (c === " " ? "" : c)).join("");
      onChange(clean);
      const nextIdx = Math.min(i + chars.length, 5);
      refs.current[nextIdx]?.focus();
      if (clean.length === 6) onComplete?.(clean);
      return;
    }
    setAt(i, v);
    if (i < 5) refs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = value.padEnd(6, " ").slice(0, 6).split("");
      if (arr[i] && arr[i] !== " ") { arr[i] = " "; }
      else if (i > 0) { arr[i - 1] = " "; refs.current[i - 1]?.focus(); }
      onChange(arr.map((c) => (c === " " ? "" : c)).join(""));
    } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const v = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!v) return;
    onChange(v);
    refs.current[Math.min(v.length, 5)]?.focus();
    if (v.length === 6) onComplete?.(v);
  };

  return (
    <div className="flex gap-2 justify-between" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          data-testid={`login-otp-${i}`}
          value={d === " " ? "" : d}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
          maxLength={6}
          disabled={success}
          autoFocus={i === 0}
          style={success ? { animation: "kairos-pop .4s ease both", animationDelay: `${i * 60}ms` } : undefined}
          className={`w-12 h-14 text-center text-2xl font-semibold tabnum rounded-lg border transition-colors focus:outline-none focus:ring-2 ${success ? "border-emerald-400 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200" : "border-gray-300 focus:ring-indigo-400 focus:border-indigo-400"}`}
        />
      ))}
    </div>
  );
}

export default function Login() {
  const { requestOtp, verifyOtp, completeLogin, sessionExpired } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);
  const successTimer = useRef(null);
  useEffect(() => () => clearTimeout(successTimer.current), []);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(null); setInfo(null); setDevOtp(null);
    try {
      const data = await requestOtp(email.trim());
      if (!data.dev_otp && data.email_delivered === false) {
        setErr(data.error || "We couldn't send your sign-in code right now. Please try again shortly, or contact your administrator.");
        return; // stay on the email step
      }
      setStep("code");
      setCooldown(30);
      if (data.dev_otp) { setDevOtp(data.dev_otp); setInfo("Email delivery isn't configured — use the dev code below."); }
      else setInfo(`We sent a 6-digit code to ${data.email}. It expires in 10 minutes.`);
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (ex) { setErr(apiError(ex, "Could not send code")); }
    finally { setBusy(false); }
  };

  const verify = async (e, codeOverride) => {
    e?.preventDefault?.();
    const c = (codeOverride ?? code).trim();
    if (c.length < 6 || busy || success) return;
    setBusy(true); setErr(null);
    try {
      const data = await verifyOtp(email.trim(), c);
      setSuccess(true);
      setInfo(null);
      successTimer.current = setTimeout(() => completeLogin(data.access_token), 1000);
    } catch (ex) {
      setErr(apiError(ex, "Verification failed"));
      setCode("");
      setBusy(false);
    }
  };

  const reset = () => { setStep("email"); setCode(""); setErr(null); setInfo(null); setDevOtp(null); };

  const features = [
    "AI-powered forecasting — 15+ models, down to 30-min intervals",
    "Fairness-scored rosters, published straight to the floor",
    "Live floor control — breaks, adherence & intraday reforecasts",
    "Team chat — group channels & direct messages, built in",
    "Voice notes, photos, @mentions, replies, reactions & read receipts",
    "Agent self-service — shift swaps, leave & real-time nudges",
  ];

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row">
      {/* left brand panel */}
      <div
        className="relative md:w-[55%] min-h-[300px] md:min-h-screen overflow-hidden flex flex-col justify-between p-8 md:p-14"
        style={{ background: "linear-gradient(150deg,#4338ca 0%,#6d28d9 45%,#db2777 80%,#f97316 100%)" }}
        data-testid="login-brand-panel"
      >
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.07, backgroundImage: GRAIN }} />
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full" style={{ background: "rgba(255,255,255,.08)" }} />
        <div className="absolute bottom-10 -left-16 w-64 h-64 rounded-full" style={{ border: "1px solid rgba(255,255,255,.15)" }} />

        <div className="relative flex items-center gap-2.5">
          <img src="/kairos-logo-192.png" alt="Kairos" data-testid="login-brand-logo" className="w-11 h-11 rounded-xl shadow-lg object-contain" style={{ background: "rgba(255,255,255,.14)", backdropFilter: "blur(8px)" }} />
          <span className="text-white text-2xl font-bold tracking-tight">Kairos</span>
        </div>

        <div className="relative text-white">
          <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.08] tracking-tight">The right people, in sync,<br />at the right moment.</h1>
          <p className="mt-4 text-white/80 text-sm md:text-base max-w-sm">The all-in-one workforce and team-communication suite for contact centres — forecast demand, build fair rosters, run the live floor, and keep everyone connected with built-in team chat.</p>
          <ul className="mt-8 space-y-3 text-white/90 text-sm">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(255,255,255,.2)" }}><Check size={12} /></span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-[11px] tracking-wider text-white/60">Powered by <span className="font-semibold text-white">Zefficacy</span></p>
      </div>

      {/* right form panel */}
      <div className="md:w-[45%] flex items-center justify-center p-8 md:p-14 bg-white">
        <div className="w-full max-w-[360px]">
          <div className="flex items-center gap-2 mb-8 md:hidden">
            <img src="/kairos-logo-192.png" alt="Kairos" className="w-9 h-9 rounded-xl shadow object-contain" />
            <span className="text-xl font-bold text-gray-900">Kairos</span>
          </div>

          {sessionExpired && !err && !info && (
            <div data-testid="login-session-expired" className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Your session expired — please sign in again.
            </div>
          )}
          {err && <div data-testid="login-error" className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}
          {info && !err && <div data-testid="login-info" className="mb-4 text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">{info}</div>}
          {devOtp && <div data-testid="login-dev-otp" className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Dev code: <span className="font-bold tabnum tracking-widest">{devOtp}</span></div>}

          {step === "email" ? (
            <form onSubmit={sendCode}>
              <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
              <p className="text-sm text-gray-500 mt-1.5 mb-7">Enter your work email and we'll send you a sign-in code.</p>
              <label className="text-sm font-semibold text-gray-800">Email</label>
              <div className="relative mt-2 mb-5">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  autoFocus
                  placeholder="you@company.com"
                  className="w-full h-12 pl-10 pr-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <button data-testid="login-send-code-btn" type="submit" disabled={busy || !email} className="w-full h-12 rounded-lg text-white font-semibold shadow-sm hover:opacity-95 transition disabled:opacity-50" style={BTN}>
                {busy ? "Sending…" : "Send code"}
              </button>
              <p className="text-xs text-gray-400 mt-6 text-center">Accounts are managed by your administrator.</p>
            </form>
          ) : (
            <form onSubmit={verify}>
              <h2 className="text-3xl font-bold text-gray-900">{success ? "You're in" : "Check your email"}</h2>
              <p className="text-sm text-gray-500 mt-1.5 mb-7">{success ? "Welcome to Kairos — taking you to your dashboard…" : <>Enter the 6-digit code sent to <span className="font-medium text-gray-700">{email}</span>.</>}</p>
              <label className="text-sm font-semibold text-gray-800">Code</label>
              <div className="mt-2 mb-5">
                <OtpBoxes value={code} onChange={setCode} onComplete={(c) => verify(null, c)} success={success} />
              </div>
              <button data-testid="login-verify-btn" type="submit" disabled={busy || code.length < 6} className="w-full h-12 rounded-lg text-white font-semibold shadow-sm hover:opacity-95 transition disabled:opacity-90 inline-flex items-center justify-center gap-2" style={success ? { background: "linear-gradient(90deg,#059669,#10b981)" } : BTN}>
                {success ? (<><Check size={18} /> Signing you in…</>) : busy ? "Verifying…" : "Verify & sign in"}
              </button>
              {!success && (
                <div className="flex items-center justify-between text-xs pt-4">
                  <button type="button" data-testid="login-change-email-btn" onClick={reset} className="text-gray-500 hover:text-gray-700">← Change email</button>
                  <button type="button" data-testid="login-resend-btn" onClick={sendCode} disabled={busy || cooldown > 0} className="text-indigo-600 hover:text-indigo-800 disabled:opacity-40">
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
